import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tasksApi, projectsApi } from '../api/client';
import { useUIStore, useDataStore } from '../store';
import type { Task, Project } from '../types';
import {
  CheckCircle2,
  Circle,
  Clock,
  Calendar,
  Flag,
  MoreVertical,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  Folder,
  Filter,
  Search,
} from 'lucide-react';

export function ListView() {
  const { openTaskModal, setSelectedTaskId } = useUIStore();
  const { setTasks, tasks } = useDataStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  // 获取任务列表
  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['tasks', filterProject, filterStatus],
    queryFn: () => tasksApi.getAll({ 
      projectId: filterProject || undefined, 
      status: filterStatus || undefined 
    }),
  });

  // 获取项目列表
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.getAll(),
  });

  useEffect(() => {
    if (tasksData?.success && tasksData.data) {
      setTasks(tasksData.data);
    }
  }, [tasksData, setTasks]);

  const projects = projectsData?.success ? projectsData.data : [];

  // 过滤任务
  const filteredTasks = tasks.filter((task) => {
    if (!task.parentId) {
      const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    }
    return false;
  });

  // 获取子任务
  const getSubtasks = (taskId: string) => {
    return tasks.filter((t) => t.parentId === taskId);
  };

  // 切换任务展开状态
  const toggleExpand = (taskId: string) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  // 获取优先级颜色
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-amber-600 bg-amber-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  // 获取项目颜色
  const getProjectColor = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.color || '#6B7280';
  };

  const handleEditTask = (task: Task) => {
    setSelectedTaskId(task.id);
    openTaskModal(task.id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 过滤栏 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* 搜索 */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索任务..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* 项目过滤 */}
          <div className="flex items-center gap-2">
            <Folder className="w-5 h-5 text-gray-400" />
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">所有项目</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* 状态过滤 */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">所有状态</option>
              <option value="todo">待办</option>
              <option value="in_progress">进行中</option>
              <option value="completed">已完成</option>
            </select>
          </div>
        </div>
      </div>

      {/* 任务列表 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredTasks.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-lg font-medium">暂无任务</p>
            <p className="text-sm mt-1">点击"新建任务"开始创建</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTasks.map((task) => {
              const subtasks = getSubtasks(task.id);
              const hasSubtasks = subtasks.length > 0;
              const isExpanded = expandedTasks.has(task.id);

              return (
                <div key={task.id} className="group">
                  {/* 主任务 */}
                  <div className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors">
                    {/* 展开按钮 */}
                    {hasSubtasks ? (
                      <button
                        onClick={() => toggleExpand(task.id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    ) : (
                      <span className="w-6" />
                    )}

                    {/* 状态图标 */}
                    <button className="flex-shrink-0">
                      {getStatusIcon(task.status)}
                    </button>

                    {/* 任务内容 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-medium ${
                            task.status === 'completed'
                              ? 'text-gray-500 line-through'
                              : 'text-gray-900'
                          }`}
                        >
                          {task.title}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${getPriorityColor(
                            task.priority
                          )}`}
                        >
                          {task.priority === 'high'
                            ? '高'
                            : task.priority === 'medium'
                            ? '中'
                            : '低'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span
                          className="flex items-center gap-1"
                          style={{ color: getProjectColor(task.projectId) }}
                        >
                          <Folder className="w-3 h-3" />
                          {projects.find((p) => p.id === task.projectId)?.name || '无项目'}
                        </span>
                        {task.dueDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(task.dueDate).toLocaleDateString('zh-CN')}
                          </span>
                        )}
                        {hasSubtasks && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {subtasks.length} 个子任务
                          </span>
                        )}
                      </div>
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEditTask(task)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => tasksApi.delete(task.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* 子任务 */}
                  {isExpanded && hasSubtasks && (
                    <div className="bg-gray-50 pl-12 pr-4 pb-2">
                      {subtasks.map((subtask) => (
                        <div
                          key={subtask.id}
                          className="flex items-center gap-3 py-2 px-4 hover:bg-white rounded-lg transition-colors"
                        >
                          <button className="flex-shrink-0">
                            {getStatusIcon(subtask.status)}
                          </button>
                          <span
                            className={`text-sm ${
                              subtask.status === 'completed'
                                ? 'text-gray-500 line-through'
                                : 'text-gray-700'
                            }`}
                          >
                            {subtask.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
